import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { Button } from "../components/ui/button";

const serviceLinks = [
  {
    name: "Mobile App Development",
    path: "/services/mobile-app-development",
    children: [
      { name: "iOS App Development", path: "/services/mobile-app-development/ios" },
      { name: "Android App Development", path: "/services/mobile-app-development/android" },
      { name: "Hybrid & Cross-Platform", path: "/services/mobile-app-development/hybrid-cross-platform" },
      { name: "Low-Code & RMAD", path: "/services/mobile-app-development/low-code-rmad" },
    ],
  },
  { name: "Web App Development", path: "/services/web-app-development" },
  { name: "Custom Software Development", path: "/services/custom-software" },
  { name: "E-commerce Development", path: "/services/ecommerce" },
  { name: "AI & Machine Learning", path: "/services/ai-ml" },
  { name: "Security Audits", path: "/services/security-audits" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0A0A]/90 border-b border-white/10 shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="/maxiom-apps-logo.png"
              alt="Maxiom Apps"
              className="w-[240px] h-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors hover:text-[#00AEEF] ${
                location.pathname === "/" ? "text-[#00AEEF]" : "text-gray-300"
              }`}
              data-testid="nav-home"
            >
              Home
            </Link>

            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-[#00AEEF] py-2 ${
                  location.pathname.startsWith("/services") ? "text-[#00AEEF]" : "text-gray-300"
                }`}
                data-testid="nav-services-trigger"
              >
                Services <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              {servicesOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-1"
                  data-testid="services-dropdown"
                >
                  <div className="bg-[#121212] border border-white/10 rounded-xl shadow-2xl px-5 py-4 min-w-[320px]">
                    <ul className="space-y-2">
                      {serviceLinks.map((service) => (
                        <li key={service.path}>
                          <Link
                            to={service.path}
                            className="block text-sm text-gray-200 hover:text-[#00AEEF] hover:bg-white/5 rounded px-2 py-1 transition-colors"
                            data-testid={`nav-service-${service.path.split("/").pop()}`}
                          >
                            {service.name}
                          </Link>
                          {service.children && (
                            <ul className="mt-1 space-y-1 pl-4 border-l border-white/10">
                              {service.children.map((child) => (
                                <li key={child.path}>
                                  <Link
                                    to={child.path}
                                    className="block text-xs text-gray-400 hover:text-[#00AEEF] hover:bg-white/5 rounded px-2 py-1 transition-colors"
                                    data-testid={`nav-service-${child.path.split("/").pop()}`}
                                  >
                                    {child.name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/case-studies"
              className={`text-sm font-medium transition-colors hover:text-[#00AEEF] ${
                location.pathname === "/case-studies" ? "text-[#00AEEF]" : "text-gray-300"
              }`}
              data-testid="nav-case-studies"
            >
              Case Studies
            </Link>

            <Link
              to="/process"
              className={`text-sm font-medium transition-colors hover:text-[#00AEEF] ${
                location.pathname === "/process" ? "text-[#00AEEF]" : "text-gray-300"
              }`}
              data-testid="nav-process"
            >
              Process
            </Link>

            <a
              href="tel:7039429420"
              className="hidden xl:flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
              data-testid="nav-phone"
            >
              <Phone className="w-4 h-4" /> 703-942-9420
            </a>

            <Link to="/contact" data-testid="nav-cta-button">
              <Button className="bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium px-6 py-2 rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]">
                Schedule a Call
              </Button>
            </Link>
          </nav>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div
          className="lg:hidden bg-[#0A0A0A]/98 backdrop-blur-xl border-t border-white/10 animate-in slide-in-from-top-2"
          data-testid="mobile-nav"
        >
          <div className="container mx-auto px-4 py-6 space-y-4">
            <Link to="/" className="block text-white text-lg font-medium py-2" data-testid="mobile-nav-home">Home</Link>
            <div>
              <button
                className="flex items-center justify-between w-full text-white text-lg font-medium py-2"
                onClick={() => setServicesOpen(!servicesOpen)}
                data-testid="mobile-services-toggle"
              >
                Services <ChevronDown className={`w-5 h-5 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              {servicesOpen && (
                <div className="pl-4 space-y-3 mt-2">
                  {serviceLinks.map((service) => (
                    <div key={service.path}>
                      <Link
                        to={service.path}
                        className="block text-gray-200 hover:text-[#00AEEF] py-1.5 text-base transition-colors"
                        data-testid={`mobile-nav-${service.path.split("/").pop()}`}
                      >
                        {service.name}
                      </Link>
                      {service.children && (
                        <div className="mt-1 space-y-1 pl-4 border-l border-white/10">
                          {service.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              className="block text-gray-400 hover:text-[#00AEEF] py-1.5 text-sm transition-colors"
                              data-testid={`mobile-nav-${child.path.split("/").pop()}`}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Link to="/case-studies" className="block text-white text-lg font-medium py-2" data-testid="mobile-nav-case-studies">Case Studies</Link>
            <Link to="/process" className="block text-white text-lg font-medium py-2" data-testid="mobile-nav-process">Process</Link>
            <a href="tel:7039429420" className="flex items-center gap-2 text-gray-400 py-2">
              <Phone className="w-4 h-4" /> 703-942-9420
            </a>
            <Link to="/contact" className="block mt-4" data-testid="mobile-nav-cta">
              <Button className="w-full bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium py-3 rounded-md">
                Schedule a Call
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
